import React from 'react';
import { ListItem, ListItemButton, ListItemText, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import SproutIcon, { sproutIconShape } from '../Common/SproutIcon';
import { matchesPath } from '../../config/navigation';

/**
 * Single navigation link in the sidebar. `level` controls indentation:
 * 0 = top level, 1 = inside a collapsible group.
 */
const SidebarMenuItem = ({ item, level = 0, onNavigate, isSelected: customIsSelected }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    navigate(item.path);
    if (onNavigate) onNavigate(item.path);
  };

  let isSelected = customIsSelected;
  if (isSelected === undefined) {
    isSelected = item.path === '/' ? location.pathname === '/' : matchesPath(location.pathname, item.path);
  }

  return (
    <ListItem disablePadding sx={{ mb: 0.25 }}>
      <ListItemButton
        onClick={handleClick}
        selected={isSelected}
        aria-current={isSelected ? 'page' : undefined}
        sx={(theme) => {
          const c = theme.palette.sprout;
          return {
            minHeight: 36,
            py: 0.75,
            pl: level === 0 ? 1.5 : 5.25,
            pr: 1.5,
            gap: 1.25,
            color: c.supportingText,
            '&:hover': { backgroundColor: c.surfaceHovered, color: c.onSurface },
            '&.Mui-selected': {
              backgroundColor: c.primaryContainer,
              color: c.onPrimaryContainer,
              '&:hover': { backgroundColor: c.primaryContainer },
              '& .MuiListItemText-primary': { fontWeight: 700 },
            },
          };
        }}
      >
        {item.icon && level === 0 && (
          <Box sx={{ display: 'flex', color: 'inherit' }}>
            <SproutIcon icon={item.icon} size={18} />
          </Box>
        )}
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'inherit',
            noWrap: true,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

SidebarMenuItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    icon: sproutIconShape,
  }).isRequired,
  level: PropTypes.number,
  onNavigate: PropTypes.func,
  isSelected: PropTypes.bool,
};

export default SidebarMenuItem;
